// @ts-strict-ignore
import dayjs from 'dayjs';
import type { Option } from '@usealloy/component-library';

import type { WorkflowTestingSuiteState } from '~/flow/TestingSuite/redux/state';
import type {
  CreatedAt,
  DaffyReactTableQueryParams,
  LogicANDFilter,
  LogicORFilter,
  TimestampFacet,
  TimeUnit,
} from '../../types';
import {
  DEFAULT_EVALUATIONS_TABLE_COLUMNS,
  DEFAULT_EVALUATIONS_TABLE_PAGINATE_BY,
  DEFAULT_TAGS_PAGE_SIZE,
  EVALUATION_TOKEN_PATTERN,
} from './constants';


const unitsOfTime: TimeUnit[] = ['days', 'hours', 'minutes'];

/** Convert an array of date strings to a facet for the Daffy Backend; the Backend
 * understands an array of ISO strings with a start and end date in the first
 * and second positions of an array, respectively - so we return that if the user
 * has chosen an end date with a specific time, otherwise we return an an array
 * like [[30, 'minutes'], 'now']
 * @param {Array} dates - An array of strings that represents a date in some
 * recognized format (ISO8601, mm/dd/yyyy, something luxon/moment/dayjs/dateFns understands)
 * @returns {Array}
 */
export const dateRangeToFacet = (dates: WorkflowTestingSuiteState['whatIf']['filters']['createdAt']): CreatedAt => {
  const endDate = dayjs(dates[1]);
  const startDate = dayjs(dates[0]);
  // We start with days and work our way down to minutes because of how the Daffy
  // Backend expects the filter (the highest unit Python's datetime.timedelta
  // accepts are weeks but we would rather just convert to days/hours/minutes).

  if (endDate.isBefore(startDate)) {
    throw new Error('Start date cannot come after the current time!');
  }

  // FIXME: Once we add time selection to the DateRangePicker this needs to be more
  // granular - ticket is: SC-118978
  if (endDate.isSame(dayjs(), 'day')) {
    const startTimestampFacet = unitsOfTime.reduce((timestampFacet, unitOfTime) => {
      if (timestampFacet.length > 0) { return timestampFacet; }

      const timeDifference = endDate.diff(startDate, unitOfTime);

      if (timeDifference > 0) {
        return [timeDifference, unitOfTime];
      }

      return timestampFacet;
    }, []) as TimestampFacet;

    return [startTimestampFacet, 'now'];
  }

  return [startDate.toISOString(), endDate.toISOString()];
};

export const getLogicORFilter = <T>(value: T | T[]): LogicORFilter<T> => {
  const formattedValue = Array.isArray(value) ? [...value] : [value];
  return {
    should: formattedValue,
  };
};

export const getLogicANDFilter = <T>(value: T | T[]): LogicANDFilter<T> => {
  const formattedValue = Array.isArray(value) ? [...value] : [value];
  return {
    filter: formattedValue,
  };
};

/**
 * Here we use the input's length as the only determining factor for the filter to build.
 * This is not necessarily always the case in other parts of our dashboard, as you
 * might have an OR or AND filter with multiple options (in the evaluations table, for example).
 * This is not necessary for what-if though, since we always use OR filters when filtering
 * by more than one option.
 */
export const getFilterFacet = <T>(values: T[]) => (values.length === 1
  ? getLogicANDFilter(values)
  : getLogicORFilter(values));

export const getTagsQueryParams = (): DaffyReactTableQueryParams => ({
  columns: [],
  filterBy: {},
  orderBy: [
    ['tag_name', 'ASC'],
  ],
  page: 0,
  paginateBy: DEFAULT_TAGS_PAGE_SIZE,
  searchBy: {},
  json: true,
});

export type GetEvaluationsQueryParams = (
  WorkflowTestingSuiteState['whatIf']['filters']
  & Partial<DaffyReactTableQueryParams>
  & {
    workflowToken: WorkflowTestingSuiteState['shared']['workflowToken'];
    versionToTestAgainst: number;
  }
);

export const getEvaluationsQueryParams = ({
  columns,
  createdAt,
  outcome,
  tags,
  versionToTestAgainst,
  workflowToken,
  paginateBy,
  page,
}: GetEvaluationsQueryParams,
): DaffyReactTableQueryParams => {
  const outcomesNames = outcome?.map(o => o.value) ?? [];
  const tagNames = tags?.map(t => t.value) ?? [];

  return {
    columns: columns ?? DEFAULT_EVALUATIONS_TABLE_COLUMNS,
    filterBy: {
      ...(createdAt && { timestamp: dateRangeToFacet(createdAt) }),
      ...(workflowToken && { application: workflowToken }),
      ...(versionToTestAgainst && { application_version: getFilterFacet([versionToTestAgainst]) }),
      ...(
        outcomesNames
        && outcomesNames.length > 0
        && { outcome: getFilterFacet(outcomesNames) }
      ),
      ...(
        tagNames
        && tagNames.length > 0
        && { tag: getFilterFacet(tagNames) }
      ),
    },
    orderBy: [],
    page: page ?? 0,
    paginateBy: paginateBy ?? DEFAULT_EVALUATIONS_TABLE_PAGINATE_BY,
    searchBy: {},
  };
};

export const toMultiSelectOptions = (values: string[]): Option[] => values?.map(value => ({
  label: value,
  value,
}));

/**
 * Extracts the valid evaluation tokens from a token string
 * @param tokenString string of valid/invalid tokens
 * @returns array of valid tokens contained in the string
 */
export const extractTokensFromString = (
  tokenString: string,
): string[] => tokenString?.match(EVALUATION_TOKEN_PATTERN) || [];

/**
 * What-If's filters are all together instead of split up between those relevant
 * for filtering by random evals and those for specific evaluations.
 * What's returned is an object with only the filters relevant for either filtering
 * by random evals or specific evals.
 */
export const getWhatIfFiltersAsQueryParamObj = ({
  workflowToken,
  versionToTestAgainst,
  filters,
}: {
  workflowToken: string;
  versionToTestAgainst: number;
  filters: WorkflowTestingSuiteState['whatIf']['filters'];
}) => {
  const filterQueryParamsObj = getEvaluationsQueryParams({
    ...filters,
    workflowToken,
    versionToTestAgainst,
  });
  const { outcome, tag } = filterQueryParamsObj.filterBy;

  const timestamp = filters.createdAt;

  /**
   * Determining the query params
   * If random evaluations is selected only the timestamp param is needed.
   * If specific evaluations is selected, the params depend on whether there are
   * eval tokens or not.
   */
  let filterQueryParams = {};
  if (filters.sampleType === 'random') {
    filterQueryParams = { timestamp, sample_size: filters.sampleSize };
  } else if (filters.evaluationTokens?.length) {
    filterQueryParams = { evaluation_token: extractTokensFromString(filters.evaluationTokens) };
  } else {
    filterQueryParams = { timestamp, outcome, tag };
  }

  return filterQueryParams;
};

/**
 * Validates an evaluation token string
 * @param tokenString string with valid or invalid tokens
 * @returns a boolean indicating whether the string contains all valid tokens or not
 */
export const validateEvaluationTokensString = (tokenString: string): boolean => {
  /**
   * A comma-separated, optional-comma ending string that contains substrings starting with "L-"
   * and containing 20 alphanumeric characters.
   *
   * For example: L-M3XgG6PVFOj2s7Zm4Yol,L-TSSaLdh9BayooY5MvXaR  ,
   */
  if (!tokenString?.length) return true;
  const validFormatPattern = /^\s*L-[\w]{20}\s*(,\s*L-\w{20})*(\s*,?\s*)?$/;
  const isValid = validFormatPattern.test(tokenString);
  return isValid;
};


export const isPasteEvent = (
  e: React.ChangeEvent<HTMLTextAreaElement> | React.ClipboardEvent<HTMLTextAreaElement>,
) => 'clipboardData' in e;


/**
 * Grabs the value from either an onPaste or onChange event
 * @param e event
 * @returns text value from event
 */
export const getEvaluationFilterEventValue = (
  e: React.ChangeEvent<HTMLTextAreaElement> | React.ClipboardEvent<HTMLTextAreaElement>,
) => {
  if (isPasteEvent(e)) {
    return (e as React.ClipboardEvent<HTMLTextAreaElement>).clipboardData.getData('text');
  }
  // both type casts are needed because TS cannot infer the event type based on the conditional
  return (e as React.ChangeEvent<HTMLTextAreaElement>).target.value;
};

/**
 * Parses a string and returns another comma-separated string of the valid evaluations tokens
 * contained in it
 * @param tokenString string of valid and/or invalid tokens
 * @returns an array of only the valid tokens
 */
export const formatEvaluationTokenString = (
  tokenString: string,
) => {
  const trimmedTokenString = tokenString.replace(/,\s*$/, '');
  const tokens = extractTokensFromString(trimmedTokenString);
  const uniqueTokens = new Set([...tokens]);
  return [...uniqueTokens].join(',\n');
};

